import {
  getCommonMedusaParamsForBundleBillingEvents,
  sendBundleEvent,
  sendPreAuthBundleEvent,
} from "@/telemetry/medusaUtils";

const BUNDLE_EVENTS_MAP = {
  B_ACCESS_APP_CLICKED: "b_access_app_clicked",
  B_AUTO_RENEW_CHANGED: "b_auto_renew_changed",
  B_BILLING_CYCLE_UPDATED: "b_billing_cycle_updated",
  B_BILLING_DETAILS_SAVED: "b_billing_details_saved",
  B_BILLING_TAB_VIEWED: "b_billing_tab_viewed",
  B_BUY_MAILBOX_TAB_VIEWED: "b_buy_mailbox_tab_viewed",
  B_CHANGE_BILLING_CYCLE_CLICKED: "b_change_billing_cycle_clicked",
  B_CHANGE_DOMAIN_CLICKED: "b_change_domain_clicked",
  B_CHANGE_DOMAIN_REQUESTED: "b_change_domain_requested",
  B_CONTROL_PANEL_LOGGED_IN: "b_control_panel_logged_in",
  B_CONTROL_PANEL_LOGGED_OUT: "b_control_panel_logged_out",
  // Wire value stays as-is: renaming it would break the existing analytics event.
  B_CHANGE_DOMAIN_NEO_MANAGED: "b_change_domain_co_site",
  B_CONTROL_PANEL_LOGIN_BUTTON_CLICKED: "b_control_panel_login_button_clicked",
  B_DOMAIN_AVAILABILITY_CHECKED: "b_domain_availability_checked",
  B_DOMAIN_SWITCHED: "b_domain_switched",
  B_DOMAIN_SWITCHER_CLICKED: "b_domain_switcher_clicked",
  B_NEO_SITE_TAB_VIEWED: "b_neo_site_tab_viewed",
  B_ORDER_SUMMARY_VIEWED: "b_order_summary_viewed",
  B_PAYMENT_CARD_SUBMITTED: "b_payment_card_submitted",
  B_PAYMENT_CARD_VERIFICATION_FAILED: "b_payment_card_verification_failed",
  B_PAYMENT_CARD_VERIFIED: "b_payment_card_verified",
  B_PAYMENT_COMPLETED: "b_payment_completed",
  B_PAYMENT_FAILED: "b_payment_failed",
  B_PAY_NOW_CLICKED: "b_pay_now_clicked",
  B_PROCEED_PAYMENT_CLICKED: "b_proceed_payment_clicked",
  B_PURCHASE_COMPLETED: "b_purchase_completed",
  B_PURCHASE_DETAILS_ENTERED: "b_purchase_details_entered",
  B_PURCHASE_STARTED: "b_purchase_started",
  B_REFERRAL_HOOK_CLICKED: "b_referral_hook_clicked",
  B_REMOVE_CARD_CLICKED: "b_remove_card_clicked",
  B_RESTRICTED_REGION_SHOWN: "b_restricted_region_shown",
  B_SUPPORT_HOOK_CLICKED: "b_support_hook_clicked",
  B_TAB_CLICKED: "b_tab_clicked",
  B_UPDATE_CARD_CLICKED: "b_update_card_clicked",
  B_UPGRADE_TAB_VIEWED: "b_upgrade_tab_viewed",
  B_UPSELL_BUTTON_CLICKED: "b_upsell_button_clicked",
  B_UPSELL_TAB_VIEWED: "b_upsell_tab_viewed",
  CHANGE_RENEWAL_DATE_CLICKED: "change_renewal_date_clicked",
  RENEWAL_DATE_CHANGED: "renewal_date_changed",
};

class BundleEvents {
  /**
   * @param {Function} sendEvent How every event of this instance is sent — `sendBundleEvent` for
   * logged in users, `sendPreAuthBundleEvent` for flows that run before login.
   */
  constructor(sendEvent) {
    this.sendEvent = sendEvent;
  }

  logCPLoginBtnClickedEvent(payload) {
    const eventObj = {
      ...payload,
      eventName: BUNDLE_EVENTS_MAP.B_CONTROL_PANEL_LOGIN_BUTTON_CLICKED,
    };
    this.sendEvent(eventObj, false);
  }

  logCPLoggedInEvent(payload) {
    const eventObj = {
      ...payload,
      eventName: BUNDLE_EVENTS_MAP.B_CONTROL_PANEL_LOGGED_IN,
    };
    this.sendEvent(eventObj);
  }

  logCPLoggedOutEvent(payload) {
    const eventObj = {
      ...payload,
      eventName: BUNDLE_EVENTS_MAP.B_CONTROL_PANEL_LOGGED_OUT,
    };
    this.sendEvent(eventObj);
  }

  logDomainSwitcherClicked(payload) {
    const eventObj = {
      ...payload,
      eventName: BUNDLE_EVENTS_MAP.B_DOMAIN_SWITCHER_CLICKED,
    };
    this.sendEvent(eventObj);
  }

  async logDomainSwitched(payload) {
    const eventObj = {
      ...payload,
      eventName: BUNDLE_EVENTS_MAP.B_DOMAIN_SWITCHED,
    };
    await this.sendEvent(eventObj);
  }

  logChangeDomainClicked(payload) {
    const eventObj = {
      ...payload,
      eventName: BUNDLE_EVENTS_MAP.B_CHANGE_DOMAIN_CLICKED,
    };
    this.sendEvent(eventObj);
  }

  logDomainAvailabilityChecked(payload) {
    const eventObj = {
      ...payload,
      eventName: BUNDLE_EVENTS_MAP.B_DOMAIN_AVAILABILITY_CHECKED,
    };
    this.sendEvent(eventObj);
  }

  logChangeDomainRequested(payload) {
    const eventObj = {
      ...payload,
      eventName: BUNDLE_EVENTS_MAP.B_CHANGE_DOMAIN_REQUESTED,
    };
    this.sendEvent(eventObj);
  }

  logChangeDomainNeoManaged(payload) {
    const eventObj = {
      ...payload,
      eventName: BUNDLE_EVENTS_MAP.B_CHANGE_DOMAIN_NEO_MANAGED,
    };
    this.sendEvent(eventObj);
  }

  logAccessAppButtonClicked(payload) {
    const eventObj = {
      ...payload,
      eventName: BUNDLE_EVENTS_MAP.B_ACCESS_APP_CLICKED,
    };
    this.sendEvent(eventObj);
  }

  /**
   * CP tab click (mailbox-nudge analytics). Payload: `tab_name` (exact
   * English tab name) and optionally `mail_order_id` / `site_order_id`
   * (callers omit them when absent).
   */
  logTabClicked(payload) {
    const eventObj = {
      ...payload,
      eventName: BUNDLE_EVENTS_MAP.B_TAB_CLICKED,
    };
    this.sendEvent(eventObj);
  }

  logSupportHookClicked(payload) {
    const eventObj = {
      ...payload,
      eventName: BUNDLE_EVENTS_MAP.B_SUPPORT_HOOK_CLICKED,
    };
    this.sendEvent(eventObj);
  }

  logReferralHookClicked(payload) {
    const eventObj = {
      ...payload,
      eventName: BUNDLE_EVENTS_MAP.B_REFERRAL_HOOK_CLICKED,
    };
    this.sendEvent(eventObj);
  }

  logPurchaseStarted(payload, options) {
    const eventObj = {
      ...payload,
      ...getCommonMedusaParamsForBundleBillingEvents(),
      eventName: BUNDLE_EVENTS_MAP.B_PURCHASE_STARTED,
    };
    this.sendEvent(eventObj, true, options);
  }

  logAutoRenewChanged(payload) {
    const eventObj = {
      ...payload,
      eventName: BUNDLE_EVENTS_MAP.B_AUTO_RENEW_CHANGED,
    };
    this.sendEvent(eventObj);
  }

  logPurchaseDetailsEntered(payload) {
    const eventObj = {
      ...payload,
      ...getCommonMedusaParamsForBundleBillingEvents(),
      eventName: BUNDLE_EVENTS_MAP.B_PURCHASE_DETAILS_ENTERED,
    };
    this.sendEvent(eventObj);
  }

  logBuyMailboxTabViewed(payload) {
    const eventObj = {
      ...payload,
      ...getCommonMedusaParamsForBundleBillingEvents(),
      eventName: BUNDLE_EVENTS_MAP.B_BUY_MAILBOX_TAB_VIEWED,
    };
    this.sendEvent(eventObj);
  }

  logOrderSummaryViewed(payload) {
    const eventObj = {
      ...payload,
      ...getCommonMedusaParamsForBundleBillingEvents(),
      eventName: BUNDLE_EVENTS_MAP.B_ORDER_SUMMARY_VIEWED,
    };
    this.sendEvent(eventObj);
  }

  logProceedPaymentClicked(payload) {
    const eventObj = {
      ...payload,
      ...getCommonMedusaParamsForBundleBillingEvents(),
      eventName: BUNDLE_EVENTS_MAP.B_PROCEED_PAYMENT_CLICKED,
    };
    this.sendEvent(eventObj);
  }

  logBillingDetailsSaved(payload) {
    const eventObj = {
      ...payload,
      ...getCommonMedusaParamsForBundleBillingEvents(),
      eventName: BUNDLE_EVENTS_MAP.B_BILLING_DETAILS_SAVED,
    };
    this.sendEvent(eventObj);
  }

  logUpdateCardClicked(payload) {
    const eventObj = {
      ...payload,
      ...getCommonMedusaParamsForBundleBillingEvents(),
      eventName: BUNDLE_EVENTS_MAP.B_UPDATE_CARD_CLICKED,
    };
    this.sendEvent(eventObj);
  }

  logRemoveCardClicked(payload) {
    const eventObj = {
      ...payload,
      ...getCommonMedusaParamsForBundleBillingEvents(),
      eventName: BUNDLE_EVENTS_MAP.B_REMOVE_CARD_CLICKED,
    };
    this.sendEvent(eventObj);
  }

  logPaymentCardSubmitted(payload) {
    const eventObj = {
      ...payload,
      ...getCommonMedusaParamsForBundleBillingEvents(),
      eventName: BUNDLE_EVENTS_MAP.B_PAYMENT_CARD_SUBMITTED,
    };
    this.sendEvent(eventObj);
  }

  logPaymentCardVerifiedFailed(payload) {
    const eventObj = {
      ...payload,
      ...getCommonMedusaParamsForBundleBillingEvents(),
      eventName: BUNDLE_EVENTS_MAP.B_PAYMENT_CARD_VERIFICATION_FAILED,
    };
    this.sendEvent(eventObj);
  }

  logPaymentCardVerified(payload) {
    const eventObj = {
      ...payload,
      ...getCommonMedusaParamsForBundleBillingEvents(),
      eventName: BUNDLE_EVENTS_MAP.B_PAYMENT_CARD_VERIFIED,
    };
    this.sendEvent(eventObj);
  }

  logPayNowClicked(payload, options) {
    const eventObj = {
      ...payload,
      ...getCommonMedusaParamsForBundleBillingEvents(),
      eventName: BUNDLE_EVENTS_MAP.B_PAY_NOW_CLICKED,
    };
    this.sendEvent(eventObj, true, options);
  }

  logPaymentFailed(payload, options) {
    const eventObj = {
      ...payload,
      ...getCommonMedusaParamsForBundleBillingEvents(),
      eventName: BUNDLE_EVENTS_MAP.B_PAYMENT_FAILED,
    };
    this.sendEvent(eventObj, true, options);
  }

  logPurchaseComplete(payload, options) {
    const eventObj = {
      ...payload,
      ...getCommonMedusaParamsForBundleBillingEvents(),
      eventName: BUNDLE_EVENTS_MAP.B_PURCHASE_COMPLETED,
    };
    this.sendEvent(eventObj, true, options);
  }

  logPaymentComplete(payload, options) {
    const eventObj = {
      ...payload,
      ...getCommonMedusaParamsForBundleBillingEvents(),
      eventName: BUNDLE_EVENTS_MAP.B_PAYMENT_COMPLETED,
    };
    this.sendEvent(eventObj, true, options);
  }

  logChangeBillingCycleClicked(payload) {
    const eventObj = {
      ...payload,
      ...getCommonMedusaParamsForBundleBillingEvents(),
      eventName: BUNDLE_EVENTS_MAP.B_CHANGE_BILLING_CYCLE_CLICKED,
    };
    this.sendEvent(eventObj);
  }

  logBillingCycleUpdated(payload) {
    const eventObj = {
      ...payload,
      ...getCommonMedusaParamsForBundleBillingEvents(),
      eventName: BUNDLE_EVENTS_MAP.B_BILLING_CYCLE_UPDATED,
    };
    this.sendEvent(eventObj);
  }

  logUpgradeTabViewed(payload) {
    const eventObj = {
      ...payload,
      ...getCommonMedusaParamsForBundleBillingEvents(),
      eventName: BUNDLE_EVENTS_MAP.B_UPGRADE_TAB_VIEWED,
    };
    this.sendEvent(eventObj);
  }

  logBillingTabViewed() {
    const eventObj = {
      ...getCommonMedusaParamsForBundleBillingEvents(),
      eventName: BUNDLE_EVENTS_MAP.B_BILLING_TAB_VIEWED,
    };
    this.sendEvent(eventObj);
  }

  logNeoSiteTabViewed() {
    const eventObj = {
      ...getCommonMedusaParamsForBundleBillingEvents(),
      eventName: BUNDLE_EVENTS_MAP.B_NEO_SITE_TAB_VIEWED,
    };
    this.sendEvent(eventObj);
  }

  logUpsellTabViewed(payload) {
    const eventObj = {
      ...payload,
      ...getCommonMedusaParamsForBundleBillingEvents(),
      eventName: BUNDLE_EVENTS_MAP.B_UPSELL_TAB_VIEWED,
    };
    this.sendEvent(eventObj);
  }

  logUpsellButtonClicked(payload) {
    const eventObj = {
      ...payload,
      ...getCommonMedusaParamsForBundleBillingEvents(),
      eventName: BUNDLE_EVENTS_MAP.B_UPSELL_BUTTON_CLICKED,
    };
    this.sendEvent(eventObj);
  }

  logRestrictedRegionShown(payload) {
    const eventObj = {
      ...payload,
      ...getCommonMedusaParamsForBundleBillingEvents(),
      eventName: BUNDLE_EVENTS_MAP.B_RESTRICTED_REGION_SHOWN,
    };
    this.sendEvent(eventObj);
  }

  logChangeRenewalDateClicked() {
    const eventObj = {
      ...getCommonMedusaParamsForBundleBillingEvents(),
      eventName: BUNDLE_EVENTS_MAP.CHANGE_RENEWAL_DATE_CLICKED,
    };
    this.sendEvent(eventObj);
  }

  logRenewalDateChanged(payload) {
    const eventObj = {
      ...payload,
      ...getCommonMedusaParamsForBundleBillingEvents(),
      eventName: BUNDLE_EVENTS_MAP.RENEWAL_DATE_CHANGED,
    };
    this.sendEvent(eventObj);
  }
}

/**
 * Same event catalog as the default export, but every event goes to `/m/addPreAuthEvent`.
 * Use it from anything that runs before login — the login screens and the partner purchase flow.
 */
export const preAuthBundleEvents = new BundleEvents(sendPreAuthBundleEvent);

export default new BundleEvents(sendBundleEvent);
